// Draggable + resizable + minimizable floating panel.
//
// Used by the standalone Sankey full-bleed page for both side-panel tabs
// (critical / dropped / donut) and the click-to-detail surface. The panel
// starts pinned to the right edge below the HUD and can be moved anywhere
// via mouse-drag on its header; resized via the bottom-right corner; or
// collapsed to a slim pill via the `_` button.

import { useRef, useState } from 'react';
import styles from './FloatingPanel.module.css';

interface DragState {
  kind: 'drag' | 'resize';
  startX: number;
  startY: number;
  // For drag: offset of pointer from panel top-left.
  // For resize: the panel's starting width/height.
  refA: number;
  refB: number;
}

export function FloatingPanel({
  title,
  minimized,
  onToggleMinimize,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  minimized: boolean;
  onToggleMinimize: () => void;
  onClose: () => void;
  /** Open at the wide default (list-heavy panels such as Dropped / Unfilled).
   *  A user resize still wins — it is applied as inline style. */
  wide?: boolean;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left?: number; top?: number }>({});
  const [size, setSize] = useState<{ width?: number; height?: number }>({});
  const dragRef = useRef<DragState | null>(null);

  const onHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      kind: 'drag',
      startX: e.clientX,
      startY: e.clientY,
      refA: e.clientX - rect.left,
      refB: e.clientY - rect.top,
    };
    bindMove();
    e.preventDefault();
  };

  const onResizeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (minimized || !panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      kind: 'resize',
      startX: e.clientX,
      startY: e.clientY,
      refA: rect.width,
      refB: rect.height,
    };
    bindMove();
    e.preventDefault();
  };

  function bindMove() {
    const move = (e: MouseEvent) => {
      const ds = dragRef.current;
      if (!ds) return;
      if (ds.kind === 'drag') {
        const x = Math.max(0, Math.min(window.innerWidth - 60, e.clientX - ds.refA));
        const y = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - ds.refB));
        setPos({ left: x, top: y });
      } else {
        const w = Math.max(
          280,
          Math.min(window.innerWidth - 40, ds.refA + (e.clientX - ds.startX)),
        );
        const h = Math.max(
          140,
          Math.min(window.innerHeight - 40, ds.refB + (e.clientY - ds.startY)),
        );
        setSize({ width: w, height: h });
      }
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  const inlineStyle: React.CSSProperties = {
    ...pos,
    ...size,
  };

  return (
    <div
      ref={panelRef}
      className={`${styles.panel} ${wide ? styles.wide : ''} ${
        minimized ? styles.minimized : ''
      }`}
      data-testid="floating-panel"
      style={inlineStyle}
    >
      <div className={styles.hd} onMouseDown={onHeaderMouseDown}>
        <div className={styles.title} title={title}>
          {title}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleMinimize();
            }}
            title={minimized ? 'Expand' : 'Minimize'}
          >
            {minimized ? '□' : '_'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>
      {!minimized && <div className={styles.body}>{children}</div>}
      {!minimized && (
        <div
          className={styles.resize}
          onMouseDown={onResizeMouseDown}
          title="Drag to resize"
        />
      )}
    </div>
  );
}
